import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text } from '@tremor/react'
import { XMarkIcon, SparklesIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function HypothesisAIGenerationModal({ isOpen, onClose, onSubmit, loading = false }) {
    const { t, i18n } = useTranslation()
    const [currentStep, setCurrentStep] = useState(0) // Start at 0 for scope selection
    const [formResponses, setFormResponses] = useState({})
    const [error, setError] = useState(null)
    const [scope, setScope] = useState(null) // Start with null, user must select
    const [estimatedCredits, setEstimatedCredits] = useState(null)

    // Academic hypothesis questions based on scientific methodology
    const academicQuestions = {
        en: [
            "What specific theoretical framework or scientific paradigm guides your hypothesis formulation?",
            "What is the precise causal relationship you predict between your independent and dependent variables?",
            "What empirical evidence from peer-reviewed literature supports your predicted relationship?",
            "How do you operationally define and measure your key variables (constructs, indicators, scales)?",
            "What is your target population and what sampling method will ensure representativeness?",
            "What alternative rival hypotheses could explain the same phenomenon you're investigating?",
            "What confounding variables need to be controlled for in your research design?",
            "What would constitute sufficient empirical evidence to support or refute your hypothesis?",
            "How does your hypothesis contribute to existing scientific knowledge or fill a literature gap?",
            "What are the testability criteria and falsifiability conditions for your hypothesis?"
        ],
        es: [
            "¿Qué marco teórico específico o paradigma científico guía la formulación de tu hipótesis?",
            "¿Cuál es la relación causal precisa que predices entre tus variables independientes y dependientes?",
            "¿Qué evidencia empírica de literatura revisada por pares respalda tu relación predicha?",
            "¿Cómo defines y mides operacionalmente tus variables clave (constructos, indicadores, escalas)?",
            "¿Cuál es tu población objetivo y qué método de muestreo asegurará representatividad?",
            "¿Qué hipótesis rivales alternativas podrían explicar el mismo fenómeno que investigas?",
            "¿Qué variables de confusión necesitan ser controladas en tu diseño de investigación?",
            "¿Qué constituiría evidencia empírica suficiente para apoyar o refutar tu hipótesis?",
            "¿Cómo contribuye tu hipótesis al conocimiento científico existente o llena una brecha en la literatura?",
            "¿Cuáles son los criterios de comprobabilidad y condiciones de falsabilidad para tu hipótesis?"
        ],
        pt: [
            "Que marco teórico específico ou paradigma científico orienta a formulação da sua hipótese?",
            "Qual é a relação causal precisa que prediz entre as suas variáveis independentes e dependentes?",
            "Que evidência empírica de literatura revista por pares suporta a sua relação predita?",
            "Como define e mede operacionalmente as suas variáveis-chave (constructos, indicadores, escalas)?",
            "Qual é a sua população-alvo e que método de amostragem assegurará representatividade?",
            "Que hipóteses rivais alternativas poderiam explicar o mesmo fenómeno que investiga?",
            "Que variáveis de confusão precisam ser controladas no seu design de investigação?",
            "O que constituiria evidência empírica suficiente para apoiar ou refutar a sua hipótese?",
            "Como contribui a sua hipótese para o conhecimento científico existente ou preenche uma lacuna na literatura?",
            "Quais são os critérios de testabilidade e condições de falsificabilidade para a sua hipótese?"
        ]
    }

    // Business hypothesis questions for consumer behavior research
    const businessQuestions = {
        en: [
            "What specific consumer behavior pattern or decision-making process do you predict will occur?",
            "What marketing stimulus or business intervention do you expect will trigger the behavioral response?",
            "What psychological or emotional factors do you predict will mediate the consumer response?",
            "What demographic or psychographic segments do you expect will respond differently to your intervention?",
            "What contextual factors (timing, channel, environment) do you predict will influence the behavior?",
            "What competing hypotheses about consumer motivation could explain the same behavioral outcome?",
            "What consumer journey stage (awareness, consideration, purchase, retention) does your hypothesis target?",
            "What key performance indicators (KPIs) will demonstrate whether your hypothesis is correct?",
            "What consumer barriers or friction points do you predict your intervention will overcome?",
            "How do you expect your hypothesis to translate into measurable business outcomes (conversion, retention, revenue)?"
        ],
        es: [
            "¿Qué patrón específico de comportamiento del consumidor o proceso de toma de decisiones predices que ocurrirá?",
            "¿Qué estímulo de marketing o intervención empresarial esperas que desencadene la respuesta comportamental?",
            "¿Qué factores psicológicos o emocionales predices que mediarán la respuesta del consumidor?",
            "¿Qué segmentos demográficos o psicográficos esperas que respondan de manera diferente a tu intervención?",
            "¿Qué factores contextuales (tiempo, canal, entorno) predices que influirán en el comportamiento?",
            "¿Qué hipótesis competidoras sobre motivación del consumidor podrían explicar el mismo resultado comportamental?",
            "¿Qué etapa del viaje del consumidor (awareness, consideración, compra, retención) apunta tu hipótesis?",
            "¿Qué indicadores clave de rendimiento (KPIs) demostrarán si tu hipótesis es correcta?",
            "¿Qué barreras del consumidor o puntos de fricción predices que tu intervención superará?",
            "¿Cómo esperas que tu hipótesis se traduzca en resultados empresariales medibles (conversión, retención, ingresos)?"
        ],
        pt: [
            "Que padrão específico de comportamento do consumidor ou processo de tomada de decisão prediz que ocorrerá?",
            "Que estímulo de marketing ou intervenção empresarial espera que desencadeie a resposta comportamental?",
            "Que fatores psicológicos ou emocionais prediz que mediarão a resposta do consumidor?",
            "Que segmentos demográficos ou psicográficos espera que respondam de forma diferente à sua intervenção?",
            "Que fatores contextuais (tempo, canal, ambiente) prediz que influenciarão o comportamento?",
            "Que hipóteses concorrentes sobre motivação do consumidor poderiam explicar o mesmo resultado comportamental?",
            "Que fase da jornada do consumidor (awareness, consideração, compra, retenção) visa a sua hipótese?",
            "Que indicadores-chave de desempenho (KPIs) demonstrarão se a sua hipótese está correta?",
            "Que barreiras do consumidor ou pontos de atrito prediz que a sua intervenção superará?",
            "Como espera que a sua hipótese se traduza em resultados empresariais mensuráveis (conversão, retenção, receita)?"
        ]
    }

    // Step 0: Scope selection question
    const scopeQuestion = {
        en: "What type of research are you conducting?",
        es: "¿Qué tipo de investigación estás realizando?",
        pt: "Que tipo de investigação está a realizar?"
    }
    
    const scopeOptions = {
        en: {
            academic: "Academic Research",
            business: "Business Research"
        },
        es: {
            academic: "Investigación Académica",
            business: "Investigación Empresarial"
        },
        pt: {
            academic: "Investigação Académica",
            business: "Investigação Empresarial"
        }
    }
    
    
    const questions = scope === 'academic' ? academicQuestions : businessQuestions
    const currentQuestions = questions[i18n.language] || questions.en

    // Check if we can generate (scope selected + at least 3 responses with 10+ chars each)
    const answeredQuestions = Object.keys(formResponses).filter(step => 
        parseInt(step) >= 1 && formResponses[step] && formResponses[step].trim().length >= 10
    ).length
    const canGenerate = scope && answeredQuestions >= 3

    // Estimate credits needed
    const estimateCreditsForGeneration = () => {
        const responses = Object.values(formResponses).filter(response => 
            response && response.trim().length >= 10
        )
        const totalLength = responses.reduce((sum, response) => sum + response.length, 0)
        
        // Credit calculation:
        // - Base cost: 1 credit
        // - Content-based: 1 credit per ~250 characters (hypotheses are more complex than goals)
        // - Generate 3-5 suggestions: multiply by 2.0
        const baseCredits = 1
        const contentCredits = Math.ceil(totalLength / 250) * 0.5
        const totalEstimate = Math.ceil((baseCredits + contentCredits) * 2.0)
        
        return Math.max(2, Math.min(10, totalEstimate))
    }

    // Update credit estimate
    React.useEffect(() => {
        if (canGenerate) {
            setEstimatedCredits(estimateCreditsForGeneration())
        } else {
            setEstimatedCredits(null)
        }
    }, [canGenerate, formResponses])

    const handleInputChange = (value) => {
        if (currentStep === 0) {
            // Scope selection
            setScope(value)
        } else {
            setFormResponses(prev => ({
                ...prev,
                [currentStep]: value
            }))
        }
    }

    const handleNext = () => {
        // Step 0: Scope selection (required)
        if (currentStep === 0) {
            if (!scope) {
                setError(t('Research.ObjectivosHipoteses.Hypotheses.Form.RequiredField'))
                return
            }
            setError(null)
            setCurrentStep(1)
            return
        }
        
        // Steps 1-3: Required responses
        const currentResponse = formResponses[currentStep]
        if (currentStep <= 3 && (!currentResponse || currentResponse.trim().length < 10)) {
            setError(t('Research.ObjectivosHipoteses.Hypotheses.Form.ResponseTooShort'))
            return
        }
        
        setError(null)
        if (currentStep < 10) { // Now goes to 10 (0,1,2,3,4,5,6,7,8,9,10)
            setCurrentStep(currentStep + 1)
        }
    }

    const handlePrev = () => {
        setError(null)
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleSkip = () => {
        setError(null)
        if (currentStep > 3 && currentStep < 10) { // Can only skip after first 3 content questions
            setCurrentStep(currentStep + 1)
        }
    }

    const handleSubmit = async () => {
        // Only include substantial responses (skip step 0 which is scope selection)
        const responses = Object.keys(formResponses)
            .filter(step => parseInt(step) >= 1 && formResponses[step] && formResponses[step].trim().length >= 10)
            .map(step => ({
                question_number: parseInt(step), // Keep step number as-is (1, 2, 3, etc.)
                question_text: currentQuestions[parseInt(step) - 1],
                user_response: sanitizeUserInput(formResponses[step])
            }))

        if (responses.length < 3) {
            setError(t('Research.ObjectivosHipoteses.Hypotheses.Form.MinimumResponses'))
            return
        }

        try {
            setError(null)
            // Send 'all' as hypothesis type to indicate backend should generate all types
            await onSubmit(responses, scope, 'all')
            handleClose()
        } catch (err) {
            setError(err.message || t('Research.ObjectivosHipoteses.Hypotheses.Form.SubmitError'))
        }
    }

    const handleClose = () => {
        if (!loading) {
            setCurrentStep(0)
            setFormResponses({})
            setError(null)
            setScope(null)
            setEstimatedCredits(null)
            onClose()
        }
    }


    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('Research.ObjectivosHipoteses.Hypotheses.Form.Title')}
                            </Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('Research.ObjectivosHipoteses.Hypotheses.Form.Description')}
                            </Text>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Main scrollable content */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 max-w-4xl mx-auto h-full flex flex-col">
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="mb-12">
                                    {currentStep === 0 ? (
                                        // Scope selection step
                                        <>
                                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                                                {scopeQuestion[i18n.language] || scopeQuestion.en}
                                            </h2>
                                            
                                            <div className="space-y-4">
                                                <button
                                                    onClick={() => handleInputChange('academic')}
                                                    disabled={loading}
                                                    className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                                        scope === 'academic' 
                                                            ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                    <div className="font-medium text-lg">
                                                        {scopeOptions[i18n.language]?.academic || scopeOptions.en.academic}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {t('Research.ObjectivosHipoteses.Scopes.AcademicDescription')}
                                                    </div>
                                                </button>
                                                
                                                <button
                                                    onClick={() => handleInputChange('business')}
                                                    disabled={loading}
                                                    className={`w-full p-6 text-left rounded-lg border-2 transition-all ${
                                                        scope === 'business' 
                                                            ? 'border-iavisus-100 bg-iavisus-50 text-iavisus-900'
                                                            : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                                    }`}
                                                >
                                                    <div className="font-medium text-lg">
                                                        {scopeOptions[i18n.language]?.business || scopeOptions.en.business}
                                                    </div>
                                                    <div className="text-sm text-gray-500 mt-1">
                                                        {t('Research.ObjectivosHipoteses.Scopes.BusinessDescription')}
                                                    </div>
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        // Regular question steps (now starting from step 1)
                                        <>
                                            <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                                                {currentQuestions[currentStep - 1]}
                                            </h2>
                                            
                                            <div>
                                                <textarea
                                                    rows={1}
                                                    value={formResponses[currentStep] || ''}
                                                    onChange={(e) => handleInputChange(e.target.value)}
                                                    placeholder={t('Research.ObjectivosHipoteses.Hypotheses.Form.ResponsePlaceholder')}
                                                    className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors resize-none overflow-hidden"
                                                    maxLength={500}
                                                    disabled={loading}
                                                    style={{
                                                        minHeight: 'auto',
                                                        lineHeight: '1.5'
                                                    }}
                                                    onInput={(e) => {
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = e.target.scrollHeight + 'px';
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            {/* Bottom section with error and buttons */}
                            <div className="border-t border-gray-100 pt-6 mt-auto">
                                {error && (
                                    <div className="mb-4 text-center">
                                        <Text className="text-red-600 text-sm">{error}</Text>
                                    </div>
                                )}

                                <div className="flex justify-between items-center">
                                    <Button
                                        variant="secondary"
                                        onClick={handlePrev}
                                        disabled={currentStep === 0 || loading}
                                        icon={ChevronLeftIcon}
                                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                                    >
                                        {t('Common.Previous')}
                                    </Button>

                                    <div className="flex space-x-3">
                                        {currentStep > 3 && currentStep !== 0 && (
                                            <Button
                                                variant="secondary"
                                                onClick={handleSkip}
                                                disabled={loading}
                                                className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                            >
                                                {t('Common.Skip')}
                                            </Button>
                                        )}

                                        {currentStep < 10 ? (
                                            <Button
                                                onClick={handleNext}
                                                disabled={loading || (currentStep === 0 && !scope)}
                                                icon={ChevronRightIcon}
                                                iconPosition="right"
                                                className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                            >
                                                {t('Common.Next')}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={handleSubmit}
                                                icon={SparklesIcon}
                                                disabled={!canGenerate || loading}
                                                className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                            >
                                                {loading 
                                                    ? t('Common.Generating') 
                                                    : estimatedCredits 
                                                        ? `${t('Research.ObjectivosHipoteses.Hypotheses.Form.GenerateHypotheses')} (${estimatedCredits} ${t('Common.Credits')})`
                                                        : t('Research.ObjectivosHipoteses.Hypotheses.Form.GenerateHypotheses')
                                                }
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default HypothesisAIGenerationModal